import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api, useAuth } from "../contexts/AuthContext";
import { AVATARS } from "../constants/avatars";

const LOGO_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_5309bd56-751f-4453-bda3-7be8ecfcb781/artifacts/orcvyuj1_let%20see%20whos%20on.png";

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const [alias, setAlias] = useState("");
  const [avatarId, setAvatarId] = useState("");
  const [aliasStatus, setAliasStatus] = useState(null); // null | "checking" | "available" | "taken"
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  let checkTimer = null;

  const handleAliasChange = (val) => {
    setAlias(val);
    setAliasStatus(null);
    clearTimeout(checkTimer);
    if (val.length >= 2) {
      setAliasStatus("checking");
      checkTimer = setTimeout(async () => {
        try {
          const { data } = await api.get(`/auth/check-alias?alias=${encodeURIComponent(val)}`);
          setAliasStatus(data.available ? "available" : "taken");
        } catch { setAliasStatus(null); }
      }, 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!alias || !avatarId) { setError("Please choose an alias and avatar"); return; }
    if (aliasStatus === "taken") { setError("That alias is taken"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/onboarding", { alias, avatar_id: avatarId });
      updateUser(data);
      navigate("/lobby", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#07070B] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.1)_0%,transparent_60%)] pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-lg"
      >
        <div className="text-center mb-8">
          <img
            src={LOGO_URL}
            alt="Let's Check Who's On?"
            className="w-48 h-auto object-contain mx-auto drop-shadow-[0_0_25px_rgba(139,92,246,0.55)]"
          />
          <p className="text-slate-400 mt-3">Set up your identity. No real name needed.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0E0E17] border border-purple-500/20 rounded-2xl p-8 space-y-8">
          {/* Alias */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3 font-[Outfit]">
              Choose your alias
            </label>
            <div className="relative">
              <input
                data-testid="alias-input"
                type="text"
                value={alias}
                onChange={e => handleAliasChange(e.target.value)}
                placeholder="NightOwl92"
                maxLength={20}
                className="w-full bg-[#141322] border border-slate-700 rounded-xl py-3 px-4 pr-10 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-colors"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {aliasStatus === "checking" && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                {aliasStatus === "available" && <Check className="w-4 h-4 text-emerald-400" />}
                {aliasStatus === "taken" && <span className="text-red-400 text-xs">taken</span>}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">2–20 characters. This is how others will know you.</p>
          </div>

          {/* Avatar picker */}
          <div>
            <label className="block text-sm font-semibold text-white mb-3 font-[Outfit]">
              Choose your character
            </label>
            <div className="grid grid-cols-4 gap-3">
              {AVATARS.map(avatar => (
                <button
                  key={avatar.id}
                  type="button"
                  data-testid={`avatar-option-${avatar.id}`}
                  onClick={() => setAvatarId(avatar.id)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    avatarId === avatar.id
                      ? "border-cyan-400 bg-cyan-500/10 scale-105"
                      : "border-slate-700 bg-[#141322] hover:border-purple-500/50"
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${avatar.bg} overflow-hidden ring-1 ring-white/10`}>
                    <img
                      src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(avatar.seed)}&backgroundColor=transparent`}
                      alt={avatar.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs text-slate-300 text-center leading-tight font-medium">{avatar.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          {alias && avatarId && (
            <div className="bg-[#141322] rounded-xl p-4 flex items-center gap-3 border border-purple-500/20">
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${AVATARS.find(a => a.id === avatarId)?.bg} overflow-hidden ring-1 ring-white/10`}>
                <img
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(AVATARS.find(a => a.id === avatarId)?.seed || "")}&backgroundColor=transparent`}
                  alt={alias}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white">{alias}</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                </div>
                <span className="text-xs text-slate-400">Online</span>
              </div>
            </div>
          )}

          {error && <p className="text-red-400 text-sm" data-testid="onboarding-error">{error}</p>}

          <button
            data-testid="onboarding-submit-btn"
            type="submit"
            disabled={loading || !alias || !avatarId || aliasStatus === "taken"}
            className="w-full py-3.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? "Setting up..." : "Enter the Lobby"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
